import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError } from '../../../lib/api/_request.js';
import type {
  SpecFlow,
  SpecFlowCheckResult,
  SpecFlowInput,
} from '../../../lib/api/spec-requirements.js';

export interface FlowEditorProps {
  /** 編集する業務フロー */
  flow: SpecFlow;
  /** 保存中かどうか */
  saving: boolean;
  /** チェック中かどうか */
  checking: boolean;
  /** 直前のチェック結果。まだ押していなければ null */
  checkResult: SpecFlowCheckResult | null;
  /** 保存が失敗したときの原因 */
  error: unknown;
  /** 保存せずに機械チェックする */
  onCheck: (input: SpecFlowInput) => void;
  /** 保存する */
  onSave: (input: SpecFlowInput) => void;
  /** 編集をやめる */
  onCancel: () => void;
  /** 書き方のルール。呼び出し元が組み立てる（この部品は取得を持たない） */
  hint: React.ReactNode;
}

/**
 * 保存できなかった理由を人が読む形にする。API が返したメッセージはそのまま見せる
 * （入口で訳し直すと、API 側で文言が変わったときに黙ってずれる）。
 * @graph-connects none
 */
function reason(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return t('spec.error.forbidden');
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return t('spec.error.saveFailed');
}

/**
 * 業務フローを編集する。
 *
 * Mermaid はテキストで書くので、**保存を押すまで形の誤りが分からないと書き直しの往復が多い**。
 * 保存と同じ判定を通す「確かめる」を別に置き、押した結果をその場で出す。判定そのものは
 * API が持つ（画面に規則を写さない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 業務フロー（Mermaid・名前・前後の接続）を編集し、保存前に機械チェックできる
 * @graph-connects ApiError [references] 保存が失敗した理由の判別
 */
export function FlowEditor({
  flow,
  saving,
  checking,
  checkResult,
  error,
  onCheck,
  onSave,
  onCancel,
  hint,
}: FlowEditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SpecFlowInput>({
    title: flow.title,
    mermaid: flow.mermaid,
    before: flow.before,
    after: flow.after,
  });

  const set = (key: keyof SpecFlowInput, value: string) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
    >
      <label className="block">
        <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
          {t('spec.flow.fields.title')}
        </span>
        <input
          className="mt-1 block w-full border border-[var(--color-border)] rounded px-3 py-2 text-sm"
          value={draft.title}
          aria-label={t('spec.flow.fields.titleAria')}
          onChange={(e) => {
            set('title', e.target.value);
          }}
        />
      </label>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
            {t('spec.flow.fields.before')}
          </span>
          <input
            className="mt-1 block w-full border border-[var(--color-border)] rounded px-3 py-2 text-sm"
            value={draft.before}
            aria-label={t('spec.flow.fields.beforeAria')}
            onChange={(e) => {
              set('before', e.target.value);
            }}
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
            {t('spec.flow.fields.after')}
          </span>
          <input
            className="mt-1 block w-full border border-[var(--color-border)] rounded px-3 py-2 text-sm"
            value={draft.after}
            aria-label={t('spec.flow.fields.afterAria')}
            onChange={(e) => {
              set('after', e.target.value);
            }}
          />
        </label>
      </div>

      {hint}

      <label className="block mt-3">
        <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
          {t('spec.flow.fields.mermaid')}
        </span>
        <textarea
          className="mt-1 block w-full border border-[var(--color-border)] rounded px-3 py-2 text-sm font-mono"
          rows={16}
          value={draft.mermaid}
          aria-label={t('spec.flow.fields.mermaid')}
          onChange={(e) => {
            set('mermaid', e.target.value);
          }}
        />
      </label>

      {checkResult === null ? null : (
        <div
          className={`mt-3 border-l-4 px-4 py-3 rounded-r ${
            checkResult.ok
              ? 'border-[var(--color-success)] bg-[var(--color-success-muted)]'
              : 'border-[var(--color-error)] bg-[var(--color-error-muted)]'
          }`}
        >
          <h4
            className={`text-xs font-semibold ${
              checkResult.ok ? 'text-[var(--color-success-text)]' : 'text-[var(--color-error-text)]'
            }`}
          >
            {t(checkResult.ok ? 'spec.flow.check.ok' : 'spec.flow.check.ng')}
          </h4>
          {checkResult.errors.length > 0 ? (
            <ul className="mt-1 text-sm list-disc pl-5 text-[var(--color-text-primary)]">
              {checkResult.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          ) : null}
          {checkResult.warnings.length > 0 ? (
            <>
              <h5 className="mt-2 text-xs font-semibold text-[var(--color-warning-text)]">
                {t('spec.flow.check.warningsHeading')}
              </h5>
              <ul className="mt-1 text-sm list-disc pl-5 text-[var(--color-text-primary)]">
                {checkResult.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </>
          ) : null}
          <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
            {t('spec.flow.check.summary', {
              lanes: checkResult.lanes.join(' / ') || t('spec.flow.check.noLanes'),
              steps: checkResult.steps.length,
            })}
          </p>
        </div>
      )}

      {error === null || error === undefined ? null : (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error)] whitespace-pre-wrap">
          {reason(t, error)}
        </p>
      )}

      <div className="mt-4 flex gap-2 flex-wrap">
        <button
          type="button"
          disabled={checking}
          className="text-sm px-4 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          onClick={() => {
            onCheck(draft);
          }}
        >
          {t(checking ? 'spec.flow.check.running' : 'spec.flow.check.run')}
        </button>
        <button
          type="submit"
          disabled={saving}
          className="text-sm px-4 py-2 rounded bg-[var(--color-accent-primary)] text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        >
          {t(saving ? 'spec.action.saving' : 'spec.action.save')}
        </button>
        <button
          type="button"
          className="text-sm px-4 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          onClick={onCancel}
        >
          {t('spec.action.cancel')}
        </button>
      </div>
    </form>
  );
}
